// src/components/learning/MainCourseContentLearning.js
import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaInfoCircle,
} from "react-icons/fa";
import DOMPurify from "dompurify";
import { ChatBubbleLeftEllipsisIcon } from "@heroicons/react/24/solid";
import Dialog from "../../dialogs";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { baseAxios } from "../../../apis";
import { useCsrfToken } from "../../../hooks/useCsrfToken";
import useAuthedApi from "../../../hooks/useAuthedApi";
import { useCourseNavigator } from "../../../hooks/useCourseNavigator";
import {
  useCourseDetailQuery,
  useCoursesQuery,
} from "../../../hooks/useCourse";
import CourseNavBar from "../components/CourseNavbar";

// --- Components ---

const Breadcrumb = ({ lessonDetail }) => (
  <div className="my-7 mt-0">
    <p className="text-sm text-[#585F72] font-semibold">
      <Link to="/learn" className="text-[#0E0F11] hover:underline">
        Trading Track
      </Link>{" "}
      /{" "}
      <Link
        to={`/learn/subcourse/${lessonDetail?.course}`}
        className="text-[#0E0F11]/60 hover:underline"
      >
        Other Course
      </Link>{" "}
      / <span className="text-[#0E0F11]/60">{lessonDetail?.title}</span>
    </p>
  </div>
);

const Sidebar = ({ lessonDetail, activeSection, scrollToSection }) => (
  <aside className="md:w-72 flex-shrink-0">
    <h3 className="font-bold text-[#585F72] mb-4">Contents</h3>
    <nav>
      <ul className="space-y-1">
        {lessonDetail?.contents?.map((section, index) => (
          <li key={section.id}>
            <a
              href={`#${section.id}`}
              onClick={(e) => {
                e.preventDefault();
                scrollToSection(section.id);
              }}
              className={`flex items-center px-3 py-2 text-sm rounded-lg transition-colors font-semibold ${
                activeSection === section.id
                  ? "bg-[#E8DEF8] text-black"
                  : "text-black hover:bg-gray-100"
              }`}
              style={{
                boxShadow:
                  activeSection === section.id
                    ? "0 2px 0 0 #4F378A"
                    : undefined,
              }}
            >
              <span
                className={`flex-shrink-0 flex items-center justify-center w-6 h-6 rounded-md text-xs font-bold mr-3 bg-[#4F378A] text-white`}
              >
                {index + 1}
              </span>
              {section.title}
            </a>
          </li>
        ))}
      </ul>
    </nav>
  </aside>
);

const SidebarSkeleton = () => {
  // Helper to create multiple skeleton list items
  const renderSkeletonItems = (count) => {
    return Array.from({ length: count }).map((_, index) => (
      <li key={index}>
        <div className="flex items-center px-3 py-2 bg-gray-200 rounded-xl shadow">
          {/* Numbered icon skeleton */}
          <div className="flex-shrink-0 w-6 h-6 bg-gray-300 rounded-md mr-3"></div>
          {/* Text line skeleton */}
          <div className="h-4 bg-gray-300 rounded-md w-4/5"></div>
        </div>
      </li>
    ));
  };

  return (
    <aside className="md:w-72 flex-shrink-0 animate-pulse">
      {/* "Contents" title skeleton */}
      <h3 className="font-bold text-[#585F72] mb-4">Contents</h3>
      <nav>
        <ul className="space-y-3">{renderSkeletonItems(5)}</ul>
      </nav>
    </aside>
  );
};

const ContentSection = ({ section, contentEntry }) => {
  if (!contentEntry) return null;

  return (
    <section key={section.id} id={section.id} className="!pb-10 scroll-mt-20">
      <h2 className="text-2xl font-bold text-gray-900 border-b pb-2 mb-4">
        {section.title}
      </h2>
      {contentEntry.content ? (
        contentEntry.content(section)
      ) : (
        <p className="text-gray-400 italic">Content not available.</p>
      )}
    </section>
  );
};

const MainContent = ({ lessonDetail, scrollContainerRef }) => (
  <div
    ref={scrollContainerRef}
    // className="flex-grow md:h-[80vh] overflow-y-auto scroll-smooth p-5 bg-white rounded-xl"
    className="flex-grow md:h-[calc(100vh-13rem)] overflow-y-auto scroll-smooth p-5 bg-white rounded-xl"
    style={{ scrollMarginTop: "20px" }}
  >
    <p className="text-xs text-gray-400 mt-2">
      Xenorize /{" "}
      {`${lessonDetail?.duration_minutes} Min${
        lessonDetail?.duration_minutes > 1 ? "s" : ""
      }`}
    </p>
    {lessonDetail?.contents?.map((section) => {
      const contentEntry = contentData[section?.section_name];
      return (
        <ContentSection
          key={section.id}
          section={section}
          contentEntry={contentEntry}
        />
      );
    })}
  </div>
);

const MainContentSkeleton = () => {
  // Helper to create placeholder paragraphs
  const renderParagraphs = (lineCount) => {
    const lines = [];
    for (let i = 0; i < lineCount; i++) {
      // Vary the width for a more realistic look
      const widthClass =
        i % 3 === 0 ? "w-full" : i % 3 === 1 ? "w-11/12" : "w-5/6";
      lines.push(
        <div
          key={i}
          className={`h-4 bg-gray-100 rounded-xl ${widthClass}`}
        ></div>
      );
    }
    return <div className="space-y-3">{lines}</div>;
  };

  return (
    <div className="flex-grow md:h-[80vh] p-5 bg-white rounded-xl animate-pulse overflow-y-auto no-scrollbar">
      {/* Metadata skeleton */}
      <div className="flex items-center  mb-8 gap-2">
        <div className="h-4 w-[90px] bg-gray-100 rounded-xl"></div>/
        <div className="h-4 w-[100px] bg-gray-100 rounded-xl "></div>
      </div>

      {/* First Content Section Skeleton */}
      <div className="!pb-10">
        <div className="h-7 w-1/2 bg-gray-200 rounded-xl mb-6"></div>
        {renderParagraphs(4)}
      </div>

      {/* Second Content Section Skeleton */}
      <div className="!pb-10">
        <div className="h-7 w-2/3 bg-gray-200 rounded-xl mb-6"></div>
        <div className={`h-[10rem] bg-gray-100 rounded-xl w-full`}></div>
      </div>

      {/* Third Content Section Skeleton */}
      <div className="!pb-10">
        <div className="h-7 w-5/12 bg-gray-200 rounded-xl mb-6"></div>
        {renderParagraphs(5)}
      </div>
    </div>
  );
};

const MainCourseContentLearning = ({ lessonDetail, loading }) => {
  const { authedPost } = useAuthedApi();
  const [activeSection, setActiveSection] = useState(null);
  const [isLessonCompleted, setIsLessonCompleted] = useState(false);
  const scrollContainerRef = useRef(null);

  const { data: coursesDetail, isLoading: loadingCourseDetails } =
    useCourseDetailQuery(lessonDetail?.course);
  const { data: courses = [], isLoading: loadingCourses } = useCoursesQuery();

  // Hook up navigator (currentKind is 'lesson' here)
  const { prevItem, nextItem, isEndOfCourse, nextCourseId } =
    useCourseNavigator({
      coursesDetail: coursesDetail || { contents: [] },
      currentId: lessonDetail?.id || "",
      allCourses: courses || [],
      currentKind: "lesson",
      currentCourseId: lessonDetail?.course || "",
    });

  const markLessonAsComplete = async () => {
    // Guard clause to prevent errors if lessonDetail is not loaded
    if (!lessonDetail?.id) {
      console.error("Cannot mark lesson as complete: lesson ID is missing.");
      return;
    }

    try {
      // Use the simplified authedPost function from the hook
      await authedPost(
        `/api/courses/lessons/${lessonDetail.id}/complete/`,
        {} // Empty payload for this POST request
      );
      setIsLessonCompleted(true);
    } catch (error) {
      console.error(
        "Error marking lesson as complete:",
        error.response?.data || error.message
      );
    }
  };

  const handleScroll = () => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer || !lessonDetail?.contents?.length) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainer;

    if (scrollTop + clientHeight >= scrollHeight - 10) {
      setActiveSection(
        lessonDetail.contents[lessonDetail.contents.length - 1].id
      );

      if (!isLessonCompleted) {
        markLessonAsComplete(); // ✅ use baseAxios here
      }
      return;
    }

    const sections = lessonDetail.contents
      .map((section) => {
        const el = document.getElementById(section.id);
        if (!el) return null;
        return { id: section.id, offsetTop: el.getBoundingClientRect().top };
      })
      .filter(Boolean);

    const inView = sections
      .filter((sec) => sec.offsetTop <= 120)
      .sort((a, b) => b.offsetTop - a.offsetTop);

    if (inView.length && inView[0].id !== activeSection) {
      setActiveSection(inView[0].id);
    }
  };

  useEffect(() => {
    if (lessonDetail?.contents?.length) {
      setActiveSection(lessonDetail.contents[0].id);
    }

    const scrollContainer = scrollContainerRef.current;
    scrollContainer?.addEventListener("scroll", handleScroll);

    // ✅ If there's no scroll needed (i.e. content fits on screen), mark complete
    if (
      scrollContainer &&
      scrollContainer.scrollHeight <= scrollContainer.clientHeight &&
      !isLessonCompleted
    ) {
      markLessonAsComplete();
    }

    return () => scrollContainer?.removeEventListener("scroll", handleScroll);
  }, [lessonDetail]);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => setActiveSection(id), 400);
    }
  };

  return (
    <div className="w-full">
      <Breadcrumb lessonDetail={lessonDetail} />
      <div className="flex flex-col md:flex-row gap-4">
        {loading ? (
          <SidebarSkeleton />
        ) : (
          <Sidebar
            lessonDetail={lessonDetail}
            activeSection={activeSection}
            scrollToSection={scrollToSection}
          />
        )}

        {loading ? (
          <MainContentSkeleton />
        ) : (
          <div className="w-full">
            <MainContent
              lessonDetail={lessonDetail}
              scrollContainerRef={scrollContainerRef}
            />
            {!loading && (
              <CourseNavBar
                prevItem={prevItem}
                nextItem={nextItem}
                isEndOfCourse={isEndOfCourse}
                nextCourseId={nextCourseId}
                onEndFallbackRoute="/learn" /* or your track page */
                // showProgress={{ current, total }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MainCourseContentLearning;

// --- Data & Content Components ---

const contentData = {
  overview: {
    content: (section) => {
      const content = JSON.parse(section?.data);
      return (
        <p
          className="text-base text-[#585F72]"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(content?.text),
          }}
        />
      );
    },
  },
  "key-takeaways": {
    content: (section) => {
      const content = JSON.parse(section?.data);
      return (
        <div className="mt-6 p-4 bg-[#FDD5BF] border-l-2 border-[#673C22] rounded-lg text-sm">
          <h4 className="font-bold text-[#765948] mb-2">Key Takeaways</h4>
          <ul className="list-disc list-inside space-y-1 text-[#765948]">
            {content?.items?.map((item, idx) => (
              <li
                key={idx}
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(item),
                }}
              />
            ))}
          </ul>
        </div>
      );
    },
  },
  glossary: {
    content: (section) => {
      const content = JSON.parse(section?.data);
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          {content?.items?.map((item, idx) => (
            <div key={idx}>
              <h4
                className="font-bold text-[#585F72]"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(item.title),
                }}
              />
              <p
                className="text-gray-600 text-sm"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(item.subtitle),
                }}
              />
            </div>
          ))}
        </div>
      );
    },
  },
  "main-content": {
    content: (section) => {
      const content = JSON.parse(section?.data);
      return (
        <p
          className="text-base text-[#585F72]"
          dangerouslySetInnerHTML={{
            __html: DOMPurify.sanitize(content?.html),
          }}
        />
      );
    },
  },
  "q-and-a": {
    content: (section) => {
      const content = JSON.parse(section?.data);
      return (
        <div className="space-y-4">
          {content?.items?.map((item, idx) => (
            <AccordionItem
              key={idx}
              question={item?.title}
              answer={item?.subtitle}
            />
          ))}
        </div>
      );
    },
  },
  scenario: {
    content: (section) => (
      <PracticeScenarioComponent content={JSON.parse(section?.data)} />
    ),
  },
  table: {
    content: (section) => {
      const content = JSON.parse(section?.data);
      return (
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          <div
            className={`grid grid-cols-${content?.headers?.length} bg-gray-100 p-4 font-bold text-gray-700`}
          >
            {content?.headers?.map((head, idx) => (
              <div className="w-1/2" key={idx}>
                {head}
              </div>
            ))}
          </div>
          <div className="flex flex-col">
            {content?.rows?.map((row, idx) => (
              <div className="flex p-4 border-b border-gray-200" key={idx}>
                {row.map((rw, ix) => (
                  <div
                    key={ix}
                    style={{ width: `${100 / row.length}%` }}
                    className="text-sm font-semibold text-black"
                  >
                    {rw}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      );
    },
  },
};

const AccordionItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="border border-gray-200 rounded-lg">
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center p-4 text-left"
      >
        <span className="font-semibold text-[#585F72]">{question}</span>
        {isOpen ? (
          <FaChevronUp className="text-purple-600" />
        ) : (
          <FaChevronDown className="text-gray-500" />
        )}
      </div>
      {isOpen && (
        <div className="p-4 border-t border-gray-200">
          <p className="text-gray-600 text-sm">{answer}</p>
        </div>
      )}
    </div>
  );
};

const PracticeScenarioComponent = ({ content }) => {
  const [isAnswered, setIsAnswered] = useState(false);
  const [text, setText] = useState("");
  const [isCorrect, setIsCorrect] = useState(null);
  const [showImageDialog, setShowImageDialog] = useState(false);

  const videoUrl = content?.videoUrl || "";
  const isYouTube = videoUrl.includes("youtube.com");

  const getYouTubeEmbedUrl = (url) => {
    const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]+)/;
    const match = url.match(youtubeRegex);
    return match ? `https://www.youtube.com/embed/${match[1]}` : url;
  };

  const handleCheckAnswer = () => {
    const userAnswer = text.trim().toLowerCase();
    const correctAnswer = content?.qa?.answer?.trim().toLowerCase();
    setIsCorrect(userAnswer.includes(correctAnswer));
    setIsAnswered(true);
  };

  return (
    <div className="rounded-lg relative">
      <div className="bg-[#F0F0FF] rounded-lg p-6 relative z-1">
        {videoUrl && (
          <div className="w-full h-[20rem] rounded-lg mb-4 overflow-hidden bg-black">
            {isYouTube ? (
              <iframe
                src={getYouTubeEmbedUrl(videoUrl)}
                title="YouTube video player"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                width="100%"
                height="100%"
                className="rounded-lg"
              />
            ) : (
              <video
                src={videoUrl}
                controls
                className="w-full h-full object-cover rounded-lg"
              />
            )}
          </div>
        )}

        {content?.imageUrl && (
          <div
            className="w-full h-[20rem] bg-gray-300 rounded-lg overflow-hidden mb-6 cursor-pointer"
            onClick={() => setShowImageDialog(true)}
          >
            <img
              src={content.imageUrl}
              alt="Related visual"
              className="w-full h-full object-cover transition-transform duration-150 hover:scale-105"
            />
          </div>
        )}

        <div className="text-gray-700 text-sm">
          <p
            className="font-semibold mb-2"
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(content?.description),
            }}
          />
          <ul className="list-disc list-inside space-y-1 mb-4">
            {content?.points?.map((point, idx) => (
              <li key={idx}>{point}</li>
            ))}
          </ul>
          <p className="font-semibold text-lg mb-3 text-[#2A122C]">
            {content?.qa?.question}
          </p>
          <textarea
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-400 focus:border-transparent"
            rows="4"
            placeholder="Field Your Answer..."
            onChange={(e) => setText(e.target.value)}
            value={text}
            readOnly={isAnswered}
          />
          {isAnswered ? (
            <div
              className={`p-3 mt-4 rounded-lg text-sm font-semibold ${
                isCorrect
                  ? "bg-green-100 text-green-800 border-l-4 border-green-500"
                  : "bg-red-100 text-red-800 border-l-4 border-red-500"
              }`}
            >
              {isCorrect
                ? "Correct! Great job."
                : "Incorrect. Review the scenario and try again."}
            </div>
          ) : (
            <div className="text-right mt-4">
              <button
                onClick={handleCheckAnswer}
                className="bg-purple-600 text-white font-bold py-2 px-6 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Submit
              </button>
            </div>
          )}
        </div>
      </div>

      {isAnswered && (
        <div className="p-6 pt-8 pb-4 bg-white shadow-[0px_2px_10px_#0001] rounded-lg mt-[-.5em]">
          <h4 className="font-bold text-[#585F72] flex items-center mb-2">
            <ChatBubbleLeftEllipsisIcon className="text-[#2D4E30] mr-2 w-[24px] h-[24px]" />
            Correct Answer
          </h4>
          <p className="text-sm text-[#2D4E30] font-semibold whitespace-pre-line">
            {content?.qa?.answer}
          </p>
        </div>
      )}

      {/* Custom Dialog for Fullscreen Image */}
      <div
        className={`fixed backdrop-blur-sm inset-0 z-[99999] flex items-center justify-center transition-opacity duration-300 ${
          showImageDialog
            ? "opacity-100 pointer-events-auto"
            : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          className="absolute inset-0 bg-gray-500 opacity-55"
          onClick={() => setShowImageDialog(false)}
        />
        <div
          className={`relative z-10 bg-white rounded-lg shadow-xl overflow-hidden transform transition-all duration-300 ${
            showImageDialog ? "scale-100 opacity-100" : "scale-95 opacity-0"
          }`}
        >
          <button
            onClick={() => setShowImageDialog(false)}
            className="absolute top-2 right-2 text-white bg-black/50 hover:bg-black/70 rounded-full p-1 z-20"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
          <img
            src={content.imageUrl}
            alt="Preview"
            className="w-full h-auto max-h-[90vh] object-contain"
          />
        </div>
      </div>
    </div>
  );
};
